<!-- eslint-disable vue/no-v-html -->
<template>
  <nav
    :aria-label="moduleName"
    class="steps__wrapper"
  >
    <ul
      class="steps"
      :style="`--progress-percentage: ${progressPercentage}%`"
    >
      <li
        v-for="(step, index) in steps"
        :key="step.id"
        :aria-current="activeQuestion.id === step.id"
        :class="[
          'steps__item',
          { 'steps__item--blocked': hasNavigationBlocked(index, step.id) },
          { 'steps__item--current': activeQuestion.id === step.id },
          { 'steps__item--navigable': hasNavigationAllowed(index, step.id) },
          { 'steps__item--passed': isPassed(index) },
        ]"
        :tabindex="hasNavigationAllowed(index, step.id) ? '0' : null"
        @click="() => hasNavigationAllowed(index, step.id) ? navigateTo(step.id) : null"
        @keyup.enter="() => hasNavigationAllowed(index, step.id) ? navigateTo(step.id) : null"
      >
        <span
          class="is-sr-only"
          v-html="step.title"
        />
      </li>
    </ul>
  </nav>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex';
  import { storeNamespace, types } from '../constants';

  const { NAVIGATE } = types;

  export default {
    name: 'Steps',

    props: {
      moduleName: {
        type: String,
        required: true
      },
    },

    data() {
      return {
        forwardestStep: null,
      };
    },

    computed: {
      ...mapGetters(storeNamespace, [
        'activeQuestion',
        'answers',
        'questionList',
        'steps',
      ]),

      activeQuestionIndex() {
        return this.questionList.findIndex(({ id }) => id === this.activeQuestion.id);
      },

      forwardestAnswerIndex() {
        if (this.answers.length === 0) {
          return null;
        }

        const forwardestAnswer = this.answers[this.answers.length - 1];
        if (!forwardestAnswer) {
          return null;
        }

        return this.steps.findIndex((step) => step.id === forwardestAnswer.questionId);
      },

      progressPercentage() {
        if (this.forwardestAnswerIndex === null) {
          return 0;
        }

        let reference = this.forwardestAnswerIndex + 1;

        if (reference >= this.steps.length - 1) {
          reference = this.steps.length - 1;
        }

        return (100 / (this.steps.length - 1) * reference).toFixed(2);
      }
    },

    watch: {
      activeQuestionIndex(activeQuestionIndex) {
        if (this.forwardestStep < activeQuestionIndex) {
          this.forwardestStep = activeQuestionIndex;
        }
      },
    },

    methods: {
      ...mapActions('skincareV2Store', [NAVIGATE]),

      hasNavigationAllowed(index, stepId) {
        return this.isNavigable(index, stepId) && !this.hasNavigationBlocked(index, stepId);
      },

      hasNavigationBlocked(index, stepId) {
        const isActive = index === this.activeQuestionIndex;
        const isAdvanced = index < this.activeQuestionIndex;
        const isForwardest = index === this.forwardestStep;

        if (isAdvanced || (isActive && isForwardest)) {
          return false;
        }

        return this.isNavigable(index, stepId) && !this.activeQuestion.validated;
      },

      isNavigable(index, stepId) {
        if ((this.activeQuestion.id === stepId) || (this.forwardestAnswerIndex === null)) {
          return false;
        }

        return this.forwardestAnswerIndex + 1 >= index;
      },

      isPassed(index) {
        if (this.forwardestAnswerIndex === null) {
          return false;
        }

        return this.forwardestAnswerIndex + 1 >= index;
      },

      navigateTo(questionId) {
        this[NAVIGATE](questionId);
      },
    }
  };
</script>

<style scoped lang="scss">
.steps {
  align-items: center;
  display: inline-flex;
  gap: rem(30px);
  margin-top: rem(25px);
  position: relative;

  @include for('large') {
    margin: 0;
  }

  &::after,
  &::before {
    content: '';
    height: rem(1.5px);
    position: absolute;
    top: 50%;
    transform: translateY(#{rem(-1px)});
    z-index: -1;
  }

  &::after {
    background-color: var(--mcb-quiz-steps-after-background-color);
    width: var(--progress-percentage);
  }

  &::before {
    background-color: var(--mcb-quiz-steps-before-background-color);
    width: 100%;
  }

  &__item {
    background-color: var(--mcb-quiz-steps-item-background-color);
    border-radius: 50%;
    height: rem(10px);
    position: relative;
    width: rem(10px);

    .has-touch &::after {
      content: '';
      height: rem(25px);
      left: 50%;
      position: absolute;
      top: 50%;
      transform: translate(-50%, -50%);
      width: rem(25px);
    }

    &--blocked {
      cursor: not-allowed;
    }

    &--current,
    &--navigable,
    &--passed {
      background-color: var(--mcb-quiz-steps-item-passed-background-color);
    }

    &--current {
      height: rem(16px);
      width: rem(16px);
    }

    &--navigable {
      cursor: pointer;
    }
  }

  &__wrapper {
    display: flex;
    justify-content: center;
    margin-bottom: rem(25px);
  }
}
</style>
